/*
 * useOrbitTheme — light/dark theme hook scoped to the Orbit Intake.
 *
 * Light is the hard default. Dark is opt-in via the toggle only.
 * System preference is intentionally ignored — Orbit Intake always
 * opens in light mode regardless of OS dark setting. Once the user
 * flips the toggle, the choice persists in localStorage.
 *
 *   const { theme, toggle } = useOrbitTheme();
 *   <div className="orbit-intake-root" data-theme={theme}>...</div>
 */

import { useCallback, useEffect, useState } from 'react';

export type OrbitTheme = 'light' | 'dark';

const STORAGE_KEY = 'orbit:theme';

function readSavedTheme(): OrbitTheme | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // localStorage may throw under privacy mode / quota — ignore and fall through.
  }
  return null;
}

function getInitialTheme(): OrbitTheme {
  // Light is the hard default. Saved preference (from a prior toggle) wins
  // if present. System-preference is deliberately not consulted.
  return readSavedTheme() ?? 'light';
}

export function useOrbitTheme() {
  const [theme, setThemeState] = useState<OrbitTheme>(getInitialTheme);

  // Persist on change so the user's pick survives across sessions.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore privacy-mode failures
    }
  }, [theme]);

  const setTheme = useCallback((next: OrbitTheme) => {
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState(prev => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  return { theme, setTheme, toggle };
}
